
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CSOLoadingSpinner } from "@/components/ui/cso-loading-spinner";

const TeamOnboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [formData, setFormData] = useState({
    organizationName: "",
    subdomain: "",
    teamSize: "5"
  });

  useEffect(() => {
    const verifyTeamSubscription = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/auth');
          return;
        }

        // Check if user has team subscription
        const { data: subscription } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('subscription_type', 'team')
          .eq('status', 'active')
          .single();

        if (!subscription) {
          toast({
            title: "Access Denied",
            description: "You need an active team subscription to access this page.",
            variant: "destructive",
          });
          navigate('/plans');
          return;
        }

        // Check if organization already exists
        const { data: existingOrg } = await supabase
          .from('organization_memberships')
          .select('organization_id, organizations(*)')
          .eq('user_id', user.id)
          .eq('role', 'owner')
          .single();

        if (existingOrg) {
          navigate(`/organization/${existingOrg.organization_id}`);
          return;
        }

      } catch (error) {
        console.error('Error verifying team subscription:', error);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyTeamSubscription();
  }, [navigate, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create organization
      const { data: organization, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: formData.organizationName,
          slug: formData.organizationName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          subdomain: formData.subdomain,
          tier: 'professional',
          max_seats: parseInt(formData.teamSize)
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Create owner membership
      const { error: membershipError } = await supabase
        .from('organization_memberships')
        .insert({
          organization_id: organization.id,
          user_id: user.id,
          role: 'owner'
        });

      if (membershipError) throw membershipError;

      toast({
        title: "Team Created Successfully!",
        description: "Your team organization has been set up. Welcome to CSO Agent Teams!",
      });

      navigate(`/organization/${organization.id}`);

    } catch (error: any) {
      console.error('Error creating team:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create team organization",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CSOLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Set Up Your Team
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Create your team organization and get started with CSO Agent Teams
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Team Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="organizationName">Organization Name</Label>
                <Input
                  id="organizationName"
                  type="text"
                  required
                  value={formData.organizationName}
                  onChange={(e) => setFormData(prev => ({ ...prev, organizationName: e.target.value }))}
                  placeholder="Acme Corporation"
                />
              </div>

              <div>
                <Label htmlFor="subdomain">Subdomain (Optional)</Label>
                <Input
                  id="subdomain"
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value }))}
                  placeholder="acme"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Your team will be accessible at: {formData.subdomain || 'your-subdomain'}.csoagent.com
                </p>
              </div>

              <div>
                <Label htmlFor="teamSize">Team Size</Label>
                <select
                  id="teamSize"
                  value={formData.teamSize}
                  onChange={(e) => setFormData(prev => ({ ...prev, teamSize: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-cso-500 focus:border-cso-500"
                >
                  <option value="5">Up to 5 members</option>
                  <option value="10">Up to 10 members</option>
                  <option value="25">Up to 25 members</option>
                </select>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-cso-600 hover:bg-cso-700"
              >
                {isLoading ? "Creating Team..." : "Create Team"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeamOnboarding;
